// Platform Admin -> Legal Readiness Center.
//
// The founder/operator's real-world legal operations manager: what
// ConfirmPH still needs, what blocks each milestone, what is due, where to
// apply, what to bring and keep, and what to do next.
//
// It reads Task 2 (Legal & Compliance) rather than duplicating it. Anything
// Task 2 already tracks shows where it is maintained and is edited there;
// only the operating profile and a handful of obligations Task 2 has no
// home for are edited here, through Task 2's own record and verify forms.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, CalendarClock, CheckCircle2, ClipboardCheck, Compass, Download, FlaskConical,
  ListChecks, Target,
} from "lucide-react";
import { toast } from "sonner";

import { CAP, LAUNCH_STATE_LABELS, fmtDate, legalError, plGet } from "../../lib/platformLegal";
import {
  CATEGORY_LABELS, LR, MILESTONE_SHORT, lrGet, lrPatch, lrPost, lrPut, planLabel,
} from "../../lib/legalReadiness";
import { api } from "../../lib/api";
import {
  Button, Empty, ExternalActionNote, Field, Loading, Note, Section, Stat, StatusPill, Tabs,
  TonePill, inputClass,
} from "../../components/platform-legal/ui";
import { RecordModal, VerifyModal } from "../../components/platform-legal/IdentitySection";

const TABS = [
  { key: "today", label: "What to do next", icon: Compass },
  { key: "requirements", label: "Requirements", icon: ListChecks },
  { key: "scenarios", label: "What if", icon: FlaskConical },
  { key: "plans", label: "Operating plans", icon: Target },
];

const READY_TONE = "border-emerald-200 bg-emerald-50 text-emerald-800";
const BLOCKED_TONE = "border-rose-200 bg-rose-50 text-rose-700";

export default function LegalReadiness() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => lrGet("/overview")
    .then(setOverview)
    .catch((err) => toast.error(legalError(err))), []);

  useEffect(() => {
    plGet("/status")
      .then((s) => {
        setStatus(s);
        return s?.capabilities?.includes(CAP.view) ? load() : null;
      })
      .catch((err) => toast.error(legalError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const tab = params.get("tab") || "today";
  const setTab = (key) => setParams({ tab: key });

  if (loading) return <Loading label="Checking your access..." />;

  if (!status?.capabilities?.includes(CAP.view)) {
    return (
      <div data-testid="legal-readiness-page" className="space-y-4">
        <Header />
        <Section>
          <Empty testId="lr-no-access">
            You don&apos;t have access to the Legal Readiness Center. It uses the same
            compliance capabilities as Legal &amp; Compliance.
          </Empty>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="legal-readiness-page" className="space-y-4">
      <Header disclaimer={overview?.disclaimer} />
      <Tabs tabs={TABS} active={tab} onChange={setTab} testId="lr-tabs" />
      {!overview ? <Loading /> : (
        <div key={tab}>
          {tab === "today" && <TodaySection overview={overview} onOpenTab={setTab} />}
          {tab === "requirements" && (
            <RequirementsSection overview={overview} onChanged={load} />
          )}
          {tab === "scenarios" && <ScenariosSection overview={overview} />}
          {tab === "plans" && <PlansSection overview={overview} onChanged={load} />}
        </div>
      )}
    </div>
  );
}

function Header({ disclaimer }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500
                      inline-flex items-center gap-1">
        <ClipboardCheck className="w-3.5 h-3.5" /> ConfirmPH&apos;s own legal operations
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Legal Readiness Center</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        What ConfirmPH still needs before it can launch, charge, hire or take investment — where
        to apply, what to bring, what to keep, and what to do next.
      </p>
      <Note tone="slate" testId="lr-disclaimer">
        {disclaimer || "This is an internal working map, not legal advice."} Registrations,
        permits, appointments and filings happen outside ConfirmPH; this centre only tracks them.
      </Note>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What to do next
// ---------------------------------------------------------------------------
function TodaySection({ overview, onOpenTab }) {
  const { summary, milestones, next_actions: actions, deadlines, public_launch: launch } = overview;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Applies to your plans" value={summary.applicable} testId="lr-stat-applicable" />
        <Stat label="Complete" value={summary.complete} tone="text-emerald-700"
              testId="lr-stat-complete" />
        <Stat label="Outstanding" value={summary.outstanding} tone="text-rose-700"
              hint={`${summary.outstanding_external} need action outside ConfirmPH`}
              testId="lr-stat-outstanding" />
        <Stat label="Overdue deadlines" value={deadlines.overdue}
              tone={deadlines.overdue ? "text-rose-700" : "text-slate-900"}
              hint={`${deadlines.upcoming} due in the next ${deadlines.horizon_days} days`}
              testId="lr-stat-overdue" />
      </div>

      {overview.signals?.map((signal) => (
        <Note key={signal.key} tone="rose" testId={`lr-signal-${signal.key}`}>
          {signal.message}{" "}
          <button className="underline" onClick={() => onOpenTab("plans")}>Review plans</button>
        </Note>
      ))}

      <Section title="Milestones" testId="lr-milestones"
               description="Each one answers “what blocks this?”. Milestones you haven’t planned yet are
                            still answered, as if you had.">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {milestones.map((m) => (
            <div key={m.key} className="rounded-xl border border-slate-200 p-3"
                 data-testid={`lr-milestone-${m.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900">{m.label}</span>
                <TonePill tone={m.ready ? READY_TONE : BLOCKED_TONE}
                          testId={`lr-milestone-verdict-${m.key}`}>
                  {m.ready ? "Ready" : "Blocked"}
                </TonePill>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {m.blocker_count} blocking item{m.blocker_count === 1 ? "" : "s"} outstanding.
                {!m.goal_active && " Not in your current plans."}
              </p>
              {m.prerequisites_pending?.length > 0 && (
                <p className="mt-1 text-xs text-rose-700">
                  Needs {m.prerequisites_pending.map((p) => MILESTONE_SHORT[p] || p).join(", ")} first.
                </p>
              )}
              <MilestoneDetail milestoneKey={m.key} />
            </div>
          ))}
        </div>
        {launch && (
          <Note tone={launch.state === "READY_FOR_PUBLIC_PRODUCTION" ? "slate" : "amber"}
                testId="lr-task2-launch">
            Public launch also follows the Legal &amp; Compliance launch engine
            ({LAUNCH_STATE_LABELS[launch.state] || launch.state}): {launch.statement}
            {launch.other_outstanding_items?.length > 0 && (
              <> Also outstanding there: {launch.other_outstanding_items.map((i) => i.title).join(", ")}.</>
            )}{" "}
            <Link to="/admin/legal?tab=identity" className="underline">Open launch readiness</Link>
          </Note>
        )}
      </Section>

      <Section title="Next actions" testId="lr-next-actions"
               description="In order: what you can start now comes first; the rest waits on an earlier step."
               actions={<Button variant="secondary" onClick={() => onOpenTab("requirements")}>
                 All requirements <ArrowRight className="w-4 h-4" />
               </Button>}>
        {actions.length === 0 ? (
          <Empty>No outstanding actions for your current plans.</Empty>
        ) : (
          <ol className="space-y-2">
            {actions.map((a, index) => <ActionCard key={a.key} action={a} index={index} />)}
          </ol>
        )}
      </Section>

      <DeadlinesSection deadlines={deadlines} />
      {summary.sequence_warnings?.length > 0 && (
        <Section title="Out-of-order records" testId="lr-sequence-warnings">
          <ul className="space-y-1 text-sm text-amber-900">
            {summary.sequence_warnings.map((w) => (
              <li key={w.key}><span className="font-semibold">{w.title}:</span> {w.warning}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function MilestoneDetail({ milestoneKey }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const toggle = () => {
    if (!open && !data) {
      lrGet(`/milestones/${milestoneKey}`).then(setData).catch((err) => toast.error(legalError(err)));
    }
    setOpen(!open);
  };
  return (
    <div className="mt-2">
      <button onClick={toggle} className="text-xs font-medium text-slate-700 underline"
              data-testid={`lr-milestone-toggle-${milestoneKey}`}>
        {open ? "Hide what blocks this" : "What blocks this?"}
      </button>
      {open && (!data ? <Loading /> : (
        <ul className="mt-2 space-y-1" data-testid={`lr-milestone-blockers-${milestoneKey}`}>
          {data.milestone.blockers.length === 0 && (
            <li className="text-xs text-slate-500">No catalogue items outstanding.</li>
          )}
          {data.milestone.blockers.map((b) => (
            <li key={b.key} className="flex flex-wrap items-center gap-1.5 text-xs">
              <StatusPill status={b.status} />
              <span className="text-slate-800">{b.title}</span>
              {!b.ready_to_start && <span className="text-slate-500">(waiting)</span>}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}

function ActionCard({ action, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <li className="rounded-xl border border-slate-200 p-3" data-testid={`lr-action-${action.key}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-900">
            {index + 1}. {action.title}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            {action.kind === "overdue" && <TonePill tone={BLOCKED_TONE}>Overdue</TonePill>}
            <TonePill tone={action.ready_to_start ? READY_TONE : "border-slate-200 bg-slate-50 text-slate-600"}>
              {action.ready_to_start ? "Can start now" : "Waiting"}
            </TonePill>
            <StatusPill status={action.status} />
          </span>
        </div>
        <p className="mt-0.5 text-xs text-slate-600">
          {action.authority}
          {action.unblocks?.length > 0 && <> · Unblocks {action.unblocks.map((m) => MILESTONE_SHORT[m] || m).join(", ")}</>}
        </p>
      </button>
      {open && <ActionDetail item={action} />}
    </li>
  );
}

function ActionDetail({ item }) {
  return (
    <div className="mt-2 space-y-2 text-sm">
      {item.waiting_on?.length > 0 && (
        <p className="text-xs text-slate-700">
          <span className="font-semibold">Waiting on:</span>{" "}
          {item.waiting_on.map((w) => w.title).join(", ")}
        </p>
      )}
      <Detail label="Where to apply">{item.where_to_apply}</Detail>
      {item.documents_typically_required?.length > 0 && (
        <Detail label="Documents typically required">
          <ul className="list-disc pl-5">
            {item.documents_typically_required.map((d) => <li key={d}>{d}</li>)}
          </ul>
        </Detail>
      )}
      {item.evidence_to_keep?.length > 0 && (
        <Detail label="Evidence to keep">
          <ul className="list-disc pl-5">
            {item.evidence_to_keep.map((d) => <li key={d}>{d}</li>)}
          </ul>
        </Detail>
      )}
      {item.maintained_in && (
        <Detail label="Recorded in ConfirmPH at">{item.maintained_in}</Detail>
      )}
      {item.external_action && <ExternalActionNote />}
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm text-slate-800">{children}</div>
    </div>
  );
}

function DeadlinesSection({ deadlines }) {
  return (
    <Section title="Deadlines and renewals" testId="lr-deadlines" description={deadlines.note}>
      {deadlines.items.length === 0 ? (
        <Empty testId="lr-deadlines-empty">
          Nothing is scheduled in the next {deadlines.horizon_days} days. That only means no date
          has been entered — {deadlines.unscheduled_calendar_entries.length} calendar obligation(s)
          have no due date yet.
        </Empty>
      ) : (
        <ul className="space-y-2">
          {deadlines.items.map((d) => (
            <li key={`${d.kind}-${d.key}-${d.title}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border
                           border-slate-200 px-3 py-2" data-testid={`lr-deadline-${d.key}`}>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900">{d.title}</span>
                <span className="block text-xs text-slate-500">{d.maintained_in}</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-xs tabular-nums text-slate-700">{fmtDate(d.date)}</span>
                <TonePill tone={d.state === "overdue" ? BLOCKED_TONE : "border-amber-200 bg-amber-50 text-amber-800"}>
                  {d.state === "overdue" ? `${Math.abs(d.days)}d overdue` : `in ${d.days}d`}
                </TonePill>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-slate-500">
        <CalendarClock className="inline w-3.5 h-3.5 mr-1" />
        Dates are set in <Link to="/admin/legal?tab=registers" className="underline">Legal &amp;
        Compliance → Vendors &amp; corporate → Compliance calendar</Link>.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Requirements
// ---------------------------------------------------------------------------
function RequirementsSection({ overview, onChanged }) {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ milestone: "", category: "", scope: "applicable" });
  const [editing, setEditing] = useState(null);
  const [verifying, setVerifying] = useState(null);

  const load = useCallback(() => lrGet("/requirements")
    .then((d) => setRows(d.requirements))
    .catch((err) => toast.error(legalError(err))), []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => (rows || []).filter((r) =>
    (!filters.milestone || r.blocks.includes(filters.milestone))
    && (!filters.category || r.category === filters.category)
    && (filters.scope === "all" || (filters.scope === "applicable" ? r.applicable
      : filters.scope === "outstanding" ? r.outstanding : !r.applicable))), [rows, filters]);

  const refresh = () => { setEditing(null); setVerifying(null); load(); onChanged(); };

  const exportCsv = async () => {
    try {
      const res = await api.get(`${LR}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "confirmph-legal-readiness.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  if (!rows) return <Loading />;
  const meta = overview.meta;
  const toRecord = (row) => ({
    key: row.key, label: row.title, ...(row.record || {}),
    status: row.record?.status || row.status,
  });

  return (
    <Section title="Requirements" testId="lr-requirements"
             description="Every obligation ConfirmPH tracks. Items read from Legal & Compliance are
                          edited there; items owned here are recorded and verified with evidence."
             actions={overview.can_export && (
               <Button variant="secondary" onClick={exportCsv} data-testid="lr-export">
                 <Download className="w-4 h-4" /> CSV
               </Button>
             )}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <select className={inputClass} value={filters.scope} aria-label="Show"
                onChange={(e) => setFilters({ ...filters, scope: e.target.value })}
                data-testid="lr-filter-scope">
          <option value="applicable">Applies to current plans</option>
          <option value="outstanding">Outstanding only</option>
          <option value="not_applicable">Not applicable yet</option>
          <option value="all">Everything</option>
        </select>
        <select className={inputClass} value={filters.milestone} aria-label="Milestone"
                onChange={(e) => setFilters({ ...filters, milestone: e.target.value })}
                data-testid="lr-filter-milestone">
          <option value="">Any milestone</option>
          {meta.milestones.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <select className={inputClass} value={filters.category} aria-label="Category"
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                data-testid="lr-filter-category">
          <option value="">Any category</option>
          {meta.categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
        </select>
      </div>
      {shown.length === 0 ? <Empty>No requirements match.</Empty> : (
        <ul className="space-y-2">
          {shown.map((row) => (
            <RequirementCard key={row.key} row={row} canWrite={overview.can_write}
                             onEdit={() => setEditing(row)} onVerify={() => setVerifying(row)} />
          ))}
        </ul>
      )}
      {editing && (
        <RecordModal field={toRecord(editing)} onClose={() => setEditing(null)} onSaved={refresh}
                     submit={(payload) => lrPatch(`/requirements/${editing.key}`, payload)} />
      )}
      {verifying && (
        <VerifyModal field={toRecord(verifying)} onClose={() => setVerifying(null)} onSaved={refresh}
                     submit={(payload) => lrPost(`/requirements/${verifying.key}/verify`, payload)} />
      )}
    </Section>
  );
}

function RequirementCard({ row, canWrite, onEdit, onVerify }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={`rounded-xl border p-3 ${row.applicable ? "border-slate-200" : "border-dashed border-slate-200 bg-slate-50"}`}
        data-testid={`lr-req-${row.key}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-900">{row.title}</span>
          <span className="flex flex-wrap items-center gap-1.5">
            {!row.applicable && <TonePill tone="border-slate-200 bg-white text-slate-500">Not applicable yet</TonePill>}
            {row.covered_by_alternative && <TonePill tone={READY_TONE}>Alternative route done</TonePill>}
            {row.recurring && <TonePill tone="border-sky-200 bg-sky-50 text-sky-800">Recurring</TonePill>}
            <StatusPill status={row.status} />
          </span>
        </div>
        <p className="mt-0.5 text-xs text-slate-600">
          {CATEGORY_LABELS[row.category] || row.category} · {row.authority}
          {row.blocks.length > 0 && <> · Blocks {row.blocks.map((m) => MILESTONE_SHORT[m] || m).join(", ")}</>}
        </p>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-slate-700">{row.what_it_is}</p>
          {row.detail && <Note tone="slate">{row.detail}</Note>}
          {row.sequence_warning && <Note tone="amber">{row.sequence_warning}</Note>}
          <ActionDetail item={row} />
          {row.record && (
            <p className="text-xs text-slate-600" data-testid={`lr-req-record-${row.key}`}>
              {row.record.value ? <>Recorded: {row.record.value}. </> : null}
              {row.record.verified_by_name
                ? <>Verified by {row.record.verified_by_name} on {fmtDate(row.record.verified_at)}. </>
                : null}
              Evidence attached: {row.record.evidence_count}.
              {row.record.expires_at && <> Expires {fmtDate(row.record.expires_at)}.</>}
            </p>
          )}
          <Note tone="slate">{row.verify_note}</Note>
          {row.editable_here ? (
            canWrite && (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={onEdit} data-testid={`lr-req-edit-${row.key}`}>
                  Record progress
                </Button>
                <Button onClick={onVerify} data-testid={`lr-req-verify-${row.key}`}>
                  <CheckCircle2 className="w-4 h-4" /> Verify with evidence
                </Button>
              </div>
            )
          ) : (
            <p className="text-xs text-slate-500">
              Updated in {row.maintained_in}.{" "}
              {row.maintained_in?.startsWith("Legal & Compliance") && (
                <Link to="/admin/legal" className="underline">Open Legal &amp; Compliance</Link>
              )}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// What if
// ---------------------------------------------------------------------------
function ScenariosSection({ overview }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [custom, setCustom] = useState({});
  const meta = overview.meta;

  const run = async (body, key) => {
    setBusy(key);
    try {
      setResult(await lrPost("/scenarios/evaluate", body));
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <Section title="What changes if…" testId="lr-scenarios"
               description="Try a change without saving it. Nothing here touches your plans or records.">
        <div className="flex flex-wrap gap-2">
          {meta.presets.map((p) => (
            <Button key={p.key} variant="secondary" busy={busy === p.key}
                    onClick={() => run({ preset: p.key }, p.key)} data-testid={`lr-preset-${p.key}`}>
              {p.label}
            </Button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <PlanSelect label="Legal form" value={custom.entity_form}
                      options={meta.entity_forms.map((f) => [f.key, f.label])}
                      onChange={(v) => setCustom({ ...custom, entity_form: v })} />
          <PlanSelect label="Subscriptions" value={custom.paid_subscriptions}
                      options={meta.paid_states.map((s) => [s, planLabel(s)])}
                      onChange={(v) => setCustom({ ...custom, paid_subscriptions: v })} />
          <PlanSelect label="Employees" value={custom.employees}
                      options={meta.employee_states.map((s) => [s, planLabel(s)])}
                      onChange={(v) => setCustom({ ...custom, employees: v })} />
          <PlanSelect label="Equity" value={custom.equity_investment}
                      options={meta.equity_states.map((s) => [s, planLabel(s)])}
                      onChange={(v) => setCustom({ ...custom, equity_investment: v })} />
          <PlanSelect label="Foreign investor" value={custom.foreign_investor === undefined ? "" : String(custom.foreign_investor)}
                      options={[["true", "Yes"], ["false", "No"]]}
                      onChange={(v) => setCustom({ ...custom, foreign_investor: v === "" ? undefined : v === "true" })} />
        </div>
        <div className="mt-2">
          <Button busy={busy === "custom"} data-testid="lr-scenario-run"
                  onClick={() => run(Object.fromEntries(Object.entries(custom)
                    .filter(([, v]) => v !== undefined && v !== "")), "custom")}>
            Compare
          </Button>
        </div>
      </Section>

      {result && (
        <Section title="Difference from today" testId="lr-scenario-result">
          <Note tone="sky" testId="lr-scenario-not-saved">{result.note}</Note>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 480 }}>
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b">
                  <th className="py-2 pr-3">Milestone</th><th className="py-2 pr-3">Today</th>
                  <th className="py-2 pr-3">In this scenario</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.milestones.map((m) => (
                  <tr key={m.key} data-testid={`lr-scenario-milestone-${m.key}`}>
                    <td className="py-2 pr-3">{m.label}</td>
                    <td className="py-2 pr-3 text-xs">
                      {m.current_ready ? "Ready" : `${m.current_blockers} blocking`}
                      {!m.current_goal_active && " · not planned"}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {m.scenario_ready ? "Ready" : `${m.scenario_blockers} blocking`}
                      {!m.scenario_goal_active && " · not planned"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Newly applies ({result.newly_applicable.length})
              </h3>
              <ul className="mt-1 space-y-1" data-testid="lr-scenario-added">
                {result.newly_applicable.length === 0 && <li className="text-xs text-slate-500">Nothing new.</li>}
                {result.newly_applicable.map((r) => (
                  <li key={r.key} className="flex flex-wrap items-center gap-1.5 text-xs">
                    <StatusPill status={r.status} /> <span>{r.title}</span>
                    <span className="text-slate-500">· {r.authority}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                No longer applies ({result.no_longer_applicable.length})
              </h3>
              <ul className="mt-1 space-y-1" data-testid="lr-scenario-removed">
                {result.no_longer_applicable.length === 0 && <li className="text-xs text-slate-500">Nothing drops away.</li>}
                {result.no_longer_applicable.map((r) => (
                  <li key={r.key} className="text-xs">{r.title}</li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}

function PlanSelect({ label, value, options, onChange }) {
  return (
    <Field label={label}>
      <select className={inputClass} value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value="">As today</option>
        {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Operating plans
// ---------------------------------------------------------------------------
function PlansSection({ overview, onChanged }) {
  const profile = overview.profile;
  const meta = overview.meta;
  const [form, setForm] = useState({
    entity_form: profile.entity_form, entity_form_basis: profile.entity_form_basis || "",
    paid_subscriptions: profile.paid_subscriptions, paid_target_date: profile.paid_target_date || "",
    employees: profile.employees, employees_target_date: profile.employees_target_date || "",
    equity_investment: profile.equity_investment, equity_target_date: profile.equity_target_date || "",
    notes: profile.notes || "", reason: "",
  });
  const [busy, setBusy] = useState(false);
  const facts = overview.facts;

  const save = async () => {
    setBusy(true);
    try {
      await lrPut("/profile", form);
      toast.success("Plans saved.");
      onChanged();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  const disabled = !overview.can_write;
  return (
    <Section title="Operating plans" testId="lr-plans"
             description="Your plans decide which requirements apply. These are intentions, not
                          registrations — registered facts are recorded in Legal & Compliance.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Legal form" hint={`In use: ${facts.entity_form_source}`}>
          <select className={inputClass} value={form.entity_form} disabled={disabled}
                  onChange={(e) => setForm({ ...form, entity_form: e.target.value })}
                  data-testid="lr-plan-entity-form">
            {meta.entity_forms.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </Field>
        <Field label="Why this form?" hint="Required once a form is chosen.">
          <input className={inputClass} value={form.entity_form_basis} disabled={disabled}
                 onChange={(e) => setForm({ ...form, entity_form_basis: e.target.value })}
                 data-testid="lr-plan-basis" />
        </Field>
        <PlanField label="Charging for subscriptions" field="paid_subscriptions"
                   dateField="paid_target_date" states={meta.paid_states} form={form}
                   setForm={setForm} disabled={disabled} />
        <PlanField label="Employees" field="employees" dateField="employees_target_date"
                   states={meta.employee_states} form={form} setForm={setForm} disabled={disabled} />
        <PlanField label="Equity investment" field="equity_investment" dateField="equity_target_date"
                   states={meta.equity_states} form={form} setForm={setForm} disabled={disabled} />
        <div className="rounded-xl border border-slate-200 p-3" data-testid="lr-plan-foreign">
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Foreign investor
          </div>
          <p className="mt-1 text-sm text-slate-800">
            {facts.foreign_investor ? "Declared" : "None declared"}
            {facts.foreign_investor && (facts.foreign_review_complete
              ? " · counsel review complete" : " · counsel review outstanding")}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Declared in <Link to="/admin/legal?tab=registers" className="underline">Legal &amp;
            Compliance → Investor readiness</Link>, so there is only one place it can be set.
          </p>
        </div>
      </div>
      <Field label="Notes" className="mt-3">
        <textarea className={inputClass} rows={3} value={form.notes} disabled={disabled}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <Field label="Reason for this change" className="mt-3">
        <input className={inputClass} value={form.reason} disabled={disabled}
               onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      </Field>
      {disabled ? (
        <Note tone="slate">You can view these plans but not change them.</Note>
      ) : (
        <div className="mt-3 flex justify-end">
          <Button onClick={save} busy={busy} data-testid="lr-plan-save">Save plans</Button>
        </div>
      )}
      {profile.updated_at && (
        <p className="mt-2 text-xs text-slate-500">
          Last changed {fmtDate(profile.updated_at)} by {profile.updated_by_name}.
        </p>
      )}
    </Section>
  );
}

function PlanField({ label, field, dateField, states, form, setForm, disabled }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Field label={label}>
        <select className={inputClass} value={form[field]} disabled={disabled}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                data-testid={`lr-plan-${field}`}>
          {states.map((s) => <option key={s} value={s}>{planLabel(s)}</option>)}
        </select>
      </Field>
      <Field label="Target date">
        <input type="date" className={inputClass} value={form[dateField]} disabled={disabled}
               onChange={(e) => setForm({ ...form, [dateField]: e.target.value })} />
      </Field>
    </div>
  );
}

